import httpClient from './http'
import type { Configuration, CreateConfigurationDto, UpdateConfigurationDto } from '@/types'

/**
 * Serviço para gerenciamento de configurações
 */
class ConfigurationsService {
  private readonly BASE_URL = '/configurations'

  /**
   * Criar nova configuração
   */
  async create(data: CreateConfigurationDto): Promise<Configuration> {
    try {
      const response = await httpClient.post<Configuration>(this.BASE_URL, data)
      return response
    } catch (error) {
      console.error('❌ Create configuration failed:', error)
      throw error
    }
  }

  /**
   * Listar todas as configurações
   */
  async findAll(): Promise<Configuration[]> {
    try {
      const response = await httpClient.get<Configuration[]>(this.BASE_URL)
      return response
    } catch (error) {
      console.error('❌ Get configurations failed:', error)
      throw error
    }
  }

  /**
   * Obter configuração atual do aplicativo
   */
  async getCurrent(): Promise<Configuration> {
    try {
      const response = await httpClient.get<Configuration>(`${this.BASE_URL}/current`)
      return response
    } catch (error) {
      console.error('❌ Get current configuration failed:', error)
      throw error
    }
  }

  /**
   * Buscar configuração por ID
   */
  async findById(id: string): Promise<Configuration> {
    try {
      const response = await httpClient.get<Configuration>(`${this.BASE_URL}/${id}`)
      return response
    } catch (error) {
      console.error('❌ Get configuration by ID failed:', error)
      throw error
    }
  }

  /**
   * Atualizar configuração atual
   */
  async updateCurrent(data: UpdateConfigurationDto): Promise<Configuration> {
    try {
      const response = await httpClient.patch<Configuration>(`${this.BASE_URL}/current`, data)
      return response
    } catch (error) {
      console.error('❌ Update current configuration failed:', error)
      throw error
    }
  }

  /**
   * Atualizar configuração específica
   */
  async update(id: string, data: UpdateConfigurationDto): Promise<Configuration> {
    try {
      const response = await httpClient.patch<Configuration>(`${this.BASE_URL}/${id}`, data)
      return response
    } catch (error) {
      console.error('❌ Update configuration failed:', error)
      throw error
    }
  }

  /**
   * Excluir configuração
   */
  async delete(id: string): Promise<void> {
    try {
      await httpClient.delete(`${this.BASE_URL}/${id}`)
    } catch (error) {
      console.error('❌ Delete configuration failed:', error)
      throw error
    }
  }

  /**
   * Upload de logo da empresa
   */
  async uploadLogo(file: File): Promise<Configuration> {
    try {
      const formData = new FormData()
      formData.append('logo', file)
      
      // Usar axios diretamente com headers corretos
      const axiosInstance = httpClient.getInstance()
      const response = await axiosInstance.post(`${this.BASE_URL}/logo/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      // A resposta pode vir diretamente como Configuration ou dentro de data
      const result = response.data?.data || response.data
      
      return result
    } catch (error) {
      console.error('❌ Logo upload failed:', error)
      throw error
    }
  }

  /**
   * Obter URL da logo
   */
  getLogoUrl(filename: string): string {
    const axiosInstance = httpClient.getInstance()
    return `${axiosInstance.defaults.baseURL}/static/logos/${filename}`
  }

  /**
   * Remover logo atual
   */
  async removeLogo(): Promise<Configuration> {
    try {
      const response = await httpClient.delete<Configuration>(`${this.BASE_URL}/logo`)
      return response
    } catch (error) {
      console.error('❌ Logo removal failed:', error)
      throw error
    }
  }

  /**
   * Upload de logo de autenticação
   */
  async uploadLogoAuth(file: File): Promise<Configuration> {
    try {
      const formData = new FormData()
      formData.append('logo_auth', file)
      
      // Usar axios diretamente com headers corretos
      const axiosInstance = httpClient.getInstance()
      const response = await axiosInstance.post(`${this.BASE_URL}/logo-auth/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      // A resposta pode vir diretamente como Configuration ou dentro de data
      const result = response.data?.data || response.data
      
      return result
    } catch (error) {
      console.error('❌ Logo auth upload failed:', error)
      throw error
    }
  }

  /**
   * Remover logo de autenticação atual
   */
  async removeLogoAuth(): Promise<Configuration> {
    try {
      const response = await httpClient.delete<Configuration>(`${this.BASE_URL}/logo-auth`)
      return response
    } catch (error) {
      console.error('❌ Logo auth removal failed:', error)
      throw error
    }
  }
}

// Exportar instância singleton
export const configurationsService = new ConfigurationsService()
export default configurationsService